/**
 * Downloads: the installers for AI Workbench and nodes, served from this platform's own release
 * feed (docs/plans/2026-10-update-management.md). On an Enterprise AIOE every link and script
 * comes from this address, so a network that reaches only the platform can install from it, and a
 * mirrored release is served from the organisation's own storage. On Mojo Up AI Cloud it is the
 * newest release Mojo Up published.
 *
 * The form column (Layout.tsx): the system picker in the header, the two releases as numbers, then
 * a group of downloads per product (one row per file, its one Download at the end), on Cloud the
 * package managers, and a group of one-line installs. Shown signed out too (App.tsx), so it needs nothing of the shell.
 */

import { compareVersions, RELEASES_PATH, type ReleaseArtifact, type ReleaseManifest } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { ArrowDownToLine, Check, Copy, Cpu, Display, Globe } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState, type ReactNode } from 'react';

import { API_URL, routes } from '../api/client.ts';
import { Empty, ErrorNote, ListGroup, ListRow, PageColumn, PageHeader, Stat } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { isCloud } from '../edition.ts';
import { useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.downloads!;
const FEED = `${API_URL}${RELEASES_PATH}`;
/** The short install addresses (`/install/<script>`), the same scripts as the feed's. */
const INSTALL = `${API_URL}/install`;
/** The first release whose scripts take `--uninstall` (workbench D187). */
const UNINSTALL_SINCE = '0.57.0';

type Os = 'windows' | 'macos' | 'linux';
const OS: Record<Os, { title: string; platform: ReleaseArtifact['platform'] }> = {
  windows: { title: 'Windows', platform: 'win32' },
  macos: { title: 'macOS', platform: 'darwin' },
  linux: { title: 'Linux', platform: 'linux' },
};
const KIND: Record<string, string> = { nsis: 'Installer (.exe)', dmg: 'Disk image (.dmg)', zip: 'Zip archive', appimage: 'AppImage', deb: 'Debian package (.deb)', 'tar.gz': 'Tar archive' };
const ARCH: Record<string, string> = { x64: 'Intel or AMD (x64)', arm64: 'ARM (arm64)', any: 'Any' };
const megabytes = (n?: number) => (n ? `${Math.round(n / 1048576)} MB` : undefined);
const day = (iso: string) => dateLong(iso);
/** An archive is the fallback; an installer is what most people want. */
const isArchive = (a: ReleaseArtifact) => a.kind === 'zip' || a.kind === 'tar.gz';

/** A guess at the visitor's computer, so its tab is open first. */
function detectOs(): Os {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return /Windows/i.test(ua) ? 'windows' : /Mac/i.test(ua) ? 'macos' : 'linux';
}

/** The one-line installs, pointed at this platform's feed so nothing is fetched from anywhere else. */
function commands(os: Os): { node: string; workbench?: string } {
  const src = FEED;
  switch (os) {
    case 'windows':
      return { node: `$env:MOJOUP_NODE_SOURCE = '${src}'; irm ${INSTALL}/node.ps1 | iex` };
    case 'macos':
      return { node: `curl -fsSL ${INSTALL}/node.sh | MOJOUP_NODE_SOURCE=${src} sh` };
    default:
      return { node: `curl -fsSL ${INSTALL}/node.sh | MOJOUP_NODE_SOURCE=${src} sh`, workbench: `curl -fsSL ${INSTALL}/workbench.sh | MOJOUP_FEED=${src} sh` };
  }
}

/**
 * Cloud only: the package managers, which install from the public GitHub mirror of each stable workbench
 * release (Mojo-Up/ai-workbench-releases). An Enterprise page keeps to its own platform, for closed networks.
 */
/** Mojo Up Releases <releases@mojoup.com.au>, the key that signs the repository (the workbench's APT_SIGNING_KEY). */
const APT_FINGERPRINT = '7FB0 67B6 3EDC 5582 1898  CF68 AE80 0EB1 C61A 8037';
/** Debian's flat repository format, published as the assets of the fixed `apt` release on the mirror. */
const APT = [
  'sudo install -d -m 755 /etc/apt/keyrings',
  'curl -fsSL https://github.com/Mojo-Up/ai-workbench-releases/releases/download/apt/mojoup-archive-keyring.gpg | sudo tee /etc/apt/keyrings/mojoup-ai-workbench.gpg >/dev/null',
  'echo "deb [signed-by=/etc/apt/keyrings/mojoup-ai-workbench.gpg] https://github.com/Mojo-Up/ai-workbench-releases/releases/download/apt ./" | sudo tee /etc/apt/sources.list.d/mojoup-ai-workbench.list',
  'sudo apt update && sudo apt install mju-ai-workbench-desktop mojoup-node',
].join('\n');
const PACKAGES: Record<Os, { title: string; description: string; command: string }[]> = {
  windows: [
    { title: 'AI Workbench', description: 'With winget, in any terminal.', command: 'winget install MojoUp.AIWorkbench' },
    { title: 'Node', description: 'With winget, in any terminal.', command: 'winget install MojoUp.AIWorkbench.Node' },
  ],
  macos: [
    { title: 'AI Workbench', description: 'With Homebrew.', command: 'brew install --cask mojo-up/tap/ai-workbench' },
    { title: 'Node', description: 'With Homebrew.', command: 'brew install mojo-up/tap/mojoup-node' },
  ],
  linux: [
    {
      title: 'AI Workbench and the node',
      description: `On Debian or Ubuntu, from Mojo Up's APT repository. apt then updates both with everything else. To check the key, gpg --show-keys /etc/apt/keyrings/mojoup-ai-workbench.gpg should show the fingerprint ${APT_FINGERPRINT}.`,
      command: APT,
    },
    { title: 'Node', description: 'With Homebrew on Linux.', command: 'brew install mojo-up/tap/mojoup-node' },
  ],
};
const ALL_RELEASES = 'https://github.com/Mojo-Up/ai-workbench-releases/releases';

/** The same script removes what it installed, from the release that taught it to. */
const uninstall = (installCommand: string, release: ReleaseManifest | undefined) => (release && compareVersions(release.version, UNINSTALL_SINCE) >= 0 && installCommand.startsWith('curl ') ? `${installCommand} -s -- --uninstall` : undefined);

/** A command shown whole (it wraps rather than scrolls) with one copy button beside it. */
function Command({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 flex items-start gap-2">
      <pre className="mono min-w-0 flex-1 rounded-lg bg-surface-secondary px-3 py-2 text-xs break-all whitespace-pre-wrap">{text}</pre>
      <Button
        size="sm"
        variant="ghost"
        isIconOnly
        aria-label={copied ? 'Copied' : `Copy: ${label}`}
        className="text-muted"
        onPress={() => {
          void navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
}

/** One product's release for the chosen system: a row per file, its one Download at the end. */
function ReleaseGroup({ title, icon, release, platform, hint, lead }: { title: string; icon: ReactNode; release: ReleaseManifest | undefined; platform: ReleaseArtifact['platform']; hint: string; lead?: boolean }) {
  const items = release ? release.artifacts.filter((a) => a.platform === platform && a.kind !== 'image' && a.kind !== 'chart') : [];
  // One primary on the page: the first installer of the product people come for.
  const primary = lead ? items.find((a) => !isArchive(a))?.name : undefined;
  return (
    <ListGroup
      title={title}
      description={release ? `${release.version}, released ${day(release.publishedAt)}` : undefined}
      empty={
        !release ? (
          <Empty compact tone={tone} icon={icon} title="Not offered yet" body={hint} />
        ) : items.length === 0 ? (
          <Empty compact tone={tone} icon={icon} title={`No build for ${OS[platformOs(platform)].title}`} body={`${release.version} has no file for this system. Choose another at the top of the page.`} />
        ) : undefined
      }
    >
      {items.map((a) => (
        <ListRow
          key={a.name}
          icon={icon}
          title={KIND[a.kind] ?? a.kind}
          description={
            <>
              <span className="block truncate" title={a.name}>
                {[ARCH[a.arch], megabytes(a.size), a.name].filter(Boolean).join(' · ')}
              </span>
              <span className="mono block truncate" title={a.sha256}>
                SHA-256 {a.sha256}
              </span>
            </>
          }
          compactControl
          control={
            <Button size="sm" variant={a.name === primary ? 'primary' : 'secondary'} onPress={() => window.open(a.url, '_blank', 'noopener')}>
              <ArrowDownToLine className="size-3.5" aria-hidden="true" />
              Download
            </Button>
          }
        />
      ))}
    </ListGroup>
  );
}

const platformOs = (p: ReleaseArtifact['platform']): Os => (p === 'win32' ? 'windows' : p === 'darwin' ? 'macos' : 'linux');

export function DownloadsPage() {
  const workbench = useLoad(() => routes.latestRelease('workbench', 'stable'), []);
  const node = useLoad(() => routes.latestRelease('node', 'stable'), []);
  const [os, setOs] = useState<Os>(detectOs);
  const cmd = commands(os);
  const loaded = !workbench.loading && !node.loading;
  const nothing = loaded && !workbench.data && !node.data;
  const removeNode = uninstall(cmd.node, node.data);
  const removeWorkbench = cmd.workbench ? uninstall(cmd.workbench, workbench.data) : undefined;

  return (
    <PageColumn width="form">
      <PageHeader
        tone={tone}
        icon={<ArrowDownToLine />}
        title="Downloads"
        subtitle={isCloud() ? 'The current release of AI Workbench and the node, signed and listed with its digest.' : 'The release this organisation offers, served from this platform so it works on a closed network too.'}
        actions={
          <Segment aria-label="Operating system" size="sm" selectedKey={os} onSelectionChange={(k) => setOs(k as Os)}>
            <Segment.Item id="windows">Windows</Segment.Item>
            <Segment.Item id="macos">macOS</Segment.Item>
            <Segment.Item id="linux">Linux</Segment.Item>
          </Segment>
        }
      />
      <PartBoundary key="downloads" name="Downloads" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<Display />} label="AI Workbench" value={loaded ? (workbench.data?.version ?? 'None yet') : undefined} detail={workbench.data ? `Released ${day(workbench.data.publishedAt)}` : loaded ? 'No stable release is offered yet' : ''} />
          <Stat tone={tone} icon={<Cpu />} label="Node" value={loaded ? (node.data?.version ?? 'None yet') : undefined} detail={node.data ? `Released ${day(node.data.publishedAt)}` : loaded ? 'No stable release is offered yet' : ''} />
          <Stat tone={tone} icon={<Globe />} label="Served from" value={<span className="block text-card leading-snug [overflow-wrap:anywhere]" title={new URL(API_URL).hostname}>{new URL(API_URL).hostname}</span>} detail={isCloud() ? 'Mojo Up AI Cloud' : 'Allow this address on your network'} />
        </div>
        <ErrorNote error={workbench.error ?? node.error} />

        {nothing ? (
          <Empty
            tone={tone}
            icon={<ArrowDownToLine />}
            title="Nothing is offered yet"
            body={isCloud() ? 'The first stable release is on its way.' : 'This organisation has not approved a release yet, or has not pulled one from Mojo Up AI Cloud. Administrators do both on the Updates page.'}
          />
        ) : loaded ? (
          <>
            <ReleaseGroup title="AI Workbench" icon={<Display />} release={workbench.data} platform={OS[os].platform} hint="No stable AI Workbench release is offered yet." lead />
            <ReleaseGroup title="Node" icon={<Cpu />} release={node.data} platform={OS[os].platform} hint="No stable node release is offered yet." />
          </>
        ) : null}

        {isCloud() ? (
          <ListGroup title="Install with a package manager" description={`${OS[os].title}. The package manager installs and updates it for you.`}>
            {PACKAGES[os].map((p) => (
              <ListRow key={p.command} title={p.title} description={p.description}>
                <Command text={p.command} label={`install ${p.title} with a package manager`} />
              </ListRow>
            ))}
            <ListRow
              title="All releases"
              description="Every stable release, with its notes and checksums."
              compactControl
              control={
                <Button size="sm" variant="secondary" onPress={() => window.open(ALL_RELEASES, '_blank', 'noopener')}>
                  Open
                </Button>
              }
            />
          </ListGroup>
        ) : null}

        <ListGroup title="Install from a terminal" description={`${OS[os].title}. The script and the package both come from ${isCloud() ? 'AI Cloud' : 'this platform'}.`}>
          <ListRow
            title="Install a node"
            description={`${os === 'windows' ? 'In PowerShell 7, as your own user. It installs under your profile, adds mojoup-node to your PATH and starts the setup wizard.' : 'It installs under your home folder (or /opt as root), links mojoup-node onto your PATH and starts the setup wizard.'} The wizard joins the node to this organisation and asks someone who may approve nodes to approve it.`}
          >
            <Command text={cmd.node} label="install a node" />
          </ListRow>
          {removeNode ? (
            <ListRow title="Remove the node" description="The same script, told to take away what it installed.">
              <Command text={removeNode} label="remove the node" />
            </ListRow>
          ) : null}
          {cmd.workbench ? (
            <ListRow title="Install AI Workbench" description="On Linux, AI Workbench installs with one command too.">
              <Command text={cmd.workbench} label="install AI Workbench" />
            </ListRow>
          ) : null}
          {removeWorkbench ? (
            <ListRow title="Remove AI Workbench" description="The same script, told to take away what it installed.">
              <Command text={removeWorkbench} label="remove AI Workbench" />
            </ListRow>
          ) : null}
        </ListGroup>
      </PartBoundary>
    </PageColumn>
  );
}
